import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
} from '@mui/material';
import { colors } from '../theme';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import HomeIcon from '@mui/icons-material/Home';
import EvStationIcon from '@mui/icons-material/EvStation';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const dataBlue = '#2196F3';
const alertRed = '#E53935';
const inkDark = '#1a1a1a';

/* ------------------------------------------------------------------ */
/* P&EMS system schematic                                              */
/* ------------------------------------------------------------------ */

function PArrow({ d, color, dashed }) {
  return (
    <path
      d={d}
      fill="none"
      stroke={color}
      strokeWidth={dashed ? 1.5 : 2}
      strokeLinecap="round"
      className={dashed ? 'dataLine' : undefined}
      markerEnd={`url(#${dashed ? 'pmData' : 'pmPower'})`}
    />
  );
}

function NumBadge({ x, y, n, color }) {
  return (
    <g>
      <circle cx={x} cy={y} r="11" fill={color} />
      <text x={x} y={y + 4.5} textAnchor="middle" fill="#fff" fontSize="13" fontWeight="700">
        {n}
      </text>
    </g>
  );
}

function PemsDiagram() {
  return (
    <Box
      sx={{
        display: { xs: 'none', sm: 'block' },
        '& .dataLine': {
          strokeDasharray: '6 6',
          animation: 'pmDashFlow 1.1s linear infinite',
        },
        '@keyframes pmDashFlow': {
          to: { strokeDashoffset: -12 },
        },
      }}
    >
      <svg viewBox="0 0 900 540" width="100%" role="img" aria-label="P&EMS schematic: grid, CT sensor, distribution box, ESS, solar, home load and EV charger connected to the SolaCharge hub">
        <defs>
          <marker id="pmPower" markerWidth="6" markerHeight="6" refX="4.5" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill={inkDark} />
          </marker>
          <marker id="pmData" markerWidth="7" markerHeight="7" refX="4.5" refY="3.5" orient="auto">
            <path d="M0,0 L7,3.5 L0,7 Z" fill={dataBlue} />
          </marker>
        </defs>

        {/* ---------- Legend (top left) ---------- */}
        <g transform="translate(30, 30)">
          <path d="M0 0 L38 0" stroke={dataBlue} strokeWidth="1.5" className="dataLine" markerEnd="url(#pmData)" />
          <text x="48" y="5" fontSize="14" fontWeight="600" fill={inkDark}>DATA</text>
          <path d="M110 0 L148 0" stroke={inkDark} strokeWidth="2" markerEnd="url(#pmPower)" />
          <text x="158" y="5" fontSize="14" fontWeight="600" fill={inkDark}>POWER</text>
        </g>

        {/* ---------- SOLACHARGE HUB cloud (top center) ---------- */}
        <path
          d="M 370 130 Q 342 130 342 102 Q 342 78 368 74 Q 372 48 402 50 Q 418 28 452 34 Q 486 24 506 48 Q 536 46 542 74 Q 566 80 566 104 Q 566 130 538 130 Z"
          fill="#fff"
          stroke={colors.green}
          strokeWidth="3"
          strokeLinejoin="round"
        />
        <path d="M 434 80 A 19 19 0 0 1 472 80 Z" fill={colors.yellow} />
        <text x="453" y="101" textAnchor="middle" fontSize="16" fontWeight="800" style={{ letterSpacing: 0.5 }}>
          <tspan fill={inkDark}>SOLA</tspan>
          <tspan fill={colors.green}>CHARGE</tspan>
        </text>
        <text x="453" y="119" textAnchor="middle" fontSize="13" fontWeight="700" fill={inkDark} style={{ letterSpacing: 2 }}>
          HUB
        </text>

        {/* ---------- GRID (left) ---------- */}
        <g stroke={colors.darkGreen} strokeWidth="3" fill="none" strokeLinecap="round">
          <path d="M80 295 L92 235 L108 235 L120 295" />
          <path d="M84 275 L116 275 M87 255 L113 255 M84 275 L116 255 M116 275 L84 255" />
          <path d="M65 255 L135 255" strokeWidth="2.5" />
          <path d="M65 255 Q73 268 81 260 M135 255 Q127 268 119 260" strokeWidth="2" stroke={colors.green} />
        </g>
        <text x="100" y="322" textAnchor="middle" fontSize="14" fontWeight="700" fill={inkDark} style={{ letterSpacing: 1.5 }}>
          GRID
        </text>

        {/* Grid <-> DB power line with CT sensor */}
        <path d="M140 265 L292 265" stroke={inkDark} strokeWidth="2" strokeLinecap="round" />
        <PArrow d="M262 265 L290 265" color={inkDark} />
        <PArrow d="M178 265 L150 265" color={inkDark} />
        <circle cx="210" cy="265" r="16" fill="#fff" stroke={colors.green} strokeWidth="2.5" />
        <text x="210" y="222" textAnchor="middle" fontSize="12" fontWeight="700" fill={inkDark}>
          CURRENT SENSOR (CT)
        </text>
        <NumBadge x={245} y={244} n="1" color={inkDark} />

        {/* ---------- DISTRIBUTION BOX (center) ---------- */}
        <rect x="300" y="215" width="80" height="100" rx="10" fill="#fff" stroke={colors.darkGreen} strokeWidth="3" />
        <rect x="315" y="228" width="50" height="26" rx="4" fill="none" stroke={colors.green} strokeWidth="2" />
        <path d="M344 262 L332 286 L340 286 L330 306 L352 280 L343 280 L352 262 Z" fill={colors.yellow} />
        <text x="340" y="338" textAnchor="middle" fontSize="12.5" fontWeight="700" fill={inkDark}>
          DISTRIBUTION
        </text>
        <text x="340" y="354" textAnchor="middle" fontSize="12.5" fontWeight="700" fill={inkDark}>
          BOX (DB)
        </text>

        {/* DB <-> ESS two-way power */}
        <PArrow d="M390 256 L442 256" color={inkDark} />
        <PArrow d="M442 274 L390 274" color={inkDark} />
        <NumBadge x={416} y={238} n="2" color={inkDark} />

        {/* ---------- ESS (right of DB) ---------- */}
        <g>
          <rect x="450" y="225" width="30" height="75" rx="4" fill="#fff" stroke={colors.darkGreen} strokeWidth="3" />
          <rect x="457" y="240" width="16" height="8" rx="3" fill="none" stroke={colors.green} strokeWidth="2" />
          <rect x="488" y="215" width="48" height="85" rx="4" fill="#fff" stroke={colors.darkGreen} strokeWidth="3" />
          <rect x="497" y="227" width="30" height="10" rx="2" fill="none" stroke={colors.green} strokeWidth="2" />
          <circle cx="512" cy="270" r="11" fill={colors.yellow} opacity="0.9" />
        </g>
        <text x="495" y="325" textAnchor="middle" fontSize="12.5" fontWeight="700" fill={inkDark}>
          ENERGY SAVING
        </text>
        <text x="495" y="341" textAnchor="middle" fontSize="12.5" fontWeight="700" fill={inkDark}>
          SYSTEM (ESS)
        </text>

        {/* ---------- SOLAR (top right) ---------- */}
        <g>
          <circle cx="640" cy="60" r="12" fill="none" stroke={colors.yellow} strokeWidth="3" />
          <g stroke={colors.yellow} strokeWidth="2.5" strokeLinecap="round">
            <path d="M640 40 L640 34 M640 80 L640 86 M620 60 L614 60 M660 60 L666 60 M626 46 L622 42 M654 46 L658 42 M626 74 L622 78" />
          </g>
          <g stroke={colors.darkGreen} strokeWidth="2.5" fill="none">
            <path d="M680 40 L764 40 L778 84 L694 84 Z" />
            <path d="M708 40 L722 84 M736 40 L750 84 M687 55 L771 55 M690 70 L774 70" strokeWidth="1.8" />
            <path d="M734 84 L734 98 M720 98 L748 98" />
          </g>
        </g>
        <text x="722" y="120" textAnchor="middle" fontSize="14" fontWeight="700" fill={inkDark} style={{ letterSpacing: 1.5 }}>
          SOLAR SYSTEM
        </text>

        {/* Solar -> ESS power */}
        <PArrow d="M722 130 L722 172 Q722 182 710 182 L548 182 Q536 182 536 194 L536 208" color={inkDark} />
        <NumBadge x={630} y={165} n="3" color={inkDark} />

        {/* ---------- HOME LOAD (bottom right) ---------- */}
        <g stroke={colors.darkGreen} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path d="M640 430 L710 382 L780 430" />
          <path d="M652 422 L652 482 L768 482 L768 422" />
          <rect x="666" y="434" width="26" height="16" rx="4" strokeWidth="2.2" />
          <path d="M670 456 L688 456" strokeWidth="2" stroke={colors.green} />
          <circle cx="738" cy="446" r="10" strokeWidth="2.2" />
        </g>
        <text x="710" y="508" textAnchor="middle" fontSize="14" fontWeight="700" fill={inkDark} style={{ letterSpacing: 1.5 }}>
          HOME LOAD
        </text>

        {/* DB -> Home power */}
        <PArrow d="M355 318 L355 450 Q355 462 367 462 L628 462" color={inkDark} />
        <NumBadge x={490} y={444} n="4" color={inkDark} />

        {/* ---------- EV CHARGER (bottom left) ---------- */}
        <g stroke={colors.darkGreen} strokeWidth="3" fill="none">
          <rect x="300" y="402" width="52" height="90" rx="24" />
          <rect x="314" y="418" width="24" height="10" rx="4" strokeWidth="2.2" />
          <circle cx="326" cy="464" r="13" strokeWidth="2.5" />
          <circle cx="326" cy="440" r="5" fill={colors.yellow} stroke="none" />
        </g>
        <g stroke={colors.darkGreen} strokeWidth="3" fill="none" strokeLinecap="round">
          <path d="M160 478 Q162 462 178 458 L189 444 Q198 434 214 436 L232 439 Q244 443 248 455 L251 462 Q260 466 258 478 Z" />
          <circle cx="185" cy="480" r="9" />
          <circle cx="236" cy="480" r="9" />
        </g>
        <path d="M216 442 L209 454 L216 454 L207 468 L221 452 L214 452 L221 442 Z" fill={colors.yellow} />
        <PArrow d="M292 462 L272 462" color={inkDark} />
        <text x="256" y="515" textAnchor="middle" fontSize="14" fontWeight="700" fill={inkDark} style={{ letterSpacing: 1.5 }}>
          EV CHARGER
        </text>

        {/* DB -> EVC power */}
        <PArrow d="M320 318 L320 394" color={inkDark} />
        <NumBadge x={300} y={360} n="5" color={inkDark} />

        {/* ---------- Data lines ---------- */}
        {/* CT -> Hub */}
        <PArrow d="M210 246 C 210 160 260 115 334 100" color={dataBlue} dashed />
        <NumBadge x={222} y={178} n="1" color={dataBlue} />
        {/* ESS -> Hub */}
        <PArrow d="M512 206 C 530 162 552 142 558 118" color={dataBlue} dashed />
        <NumBadge x={548} y={166} n="2" color={dataBlue} />
        {/* Hub -> EVC */}
        <PArrow d="M450 134 C 450 250 420 330 352 398" color={dataBlue} dashed />
        <NumBadge x={410} y={352} n="3" color={dataBlue} />
      </svg>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* Energy flow / data flow steps                                       */
/* ------------------------------------------------------------------ */

function StepList({ title, titleColor, chipColor, steps }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 4 },
        borderRadius: 4,
        border: '1px solid #e6e6e6',
        bgcolor: '#fff',
        height: '100%',
      }}
    >
      <Typography
        variant="h5"
        sx={{ fontWeight: 800, color: titleColor, mb: 3, letterSpacing: 0.5 }}
      >
        {title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {steps.map((step, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 2 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: chipColor,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: '0.85rem',
                flexShrink: 0,
                mt: 0.2,
              }}
            >
              {i + 1}
            </Box>
            <Typography sx={{ color: '#444', fontSize: '0.95rem', lineHeight: 1.6 }}>
              {step}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

/* ------------------------------------------------------------------ */
/* DLB scenario cards                                                  */
/* ------------------------------------------------------------------ */

function UnitBar({ heading, headingColor, segments, filled }) {
  return (
    <Box>
      {heading && (
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 700,
            color: headingColor,
            letterSpacing: 0.5,
            mb: 0.75,
            textAlign: 'center',
          }}
        >
          {heading}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1.25, justifyContent: 'center' }}>
        {segments.map((seg, i) => (
          <Box key={i}>
            <Box sx={{ display: 'flex', gap: 0.4 }}>
              {Array.from({ length: seg.u }).map((_, j) => (
                <Box
                  key={j}
                  sx={{
                    width: 13,
                    height: 13,
                    borderRadius: '2px',
                    border: `2.5px solid ${seg.color}`,
                    bgcolor: filled ? seg.color : 'transparent',
                  }}
                />
              ))}
            </Box>
            <Typography
              sx={{
                fontSize: '0.68rem',
                fontWeight: 700,
                color: seg.color === colors.yellow ? '#b98f00' : seg.color,
                textAlign: 'center',
                mt: 0.5,
                lineHeight: 1.2,
              }}
            >
              {seg.label}
              <br />
              {seg.u}u
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function DlbScenarios() {
  const scenarios = [
    {
      name: 'SURPLUS',
      pill: colors.yellow,
      pillText: colors.black,
      conditions: ['Strong solar', 'Battery full'],
      supply: { heading: 'MAX PV GEN 8u', color: '#b98f00', segments: [{ label: 'PV', u: 8, color: colors.yellow }] },
      loads: [
        { label: 'HOME', u: 3, color: inkDark },
        { label: 'EVC', u: 3, color: colors.green },
        { label: 'GRID', u: 2, color: alertRed },
      ],
      checks: ['8u production', '8u fully utilized'],
    },
    {
      name: 'PV CHARGE',
      pill: colors.yellow,
      pillText: colors.black,
      conditions: ['Strong solar', 'Battery charging'],
      supply: { heading: 'MAX PV GEN 8u', color: '#b98f00', segments: [{ label: 'PV', u: 8, color: colors.yellow }] },
      loads: [
        { label: 'HOME', u: 3, color: inkDark },
        { label: 'EVC', u: 3, color: colors.green },
        { label: 'CHARGE', u: 2, color: dataBlue },
      ],
      checks: ['8u production', '8u fully utilized'],
    },
    {
      name: 'PV SHORT',
      pill: dataBlue,
      pillText: '#fff',
      conditions: ['Weak solar', 'Battery discharging'],
      supply: {
        heading: 'PV 1u + BATTERY 5u',
        color: dataBlue,
        segments: [
          { label: 'PV', u: 1, color: colors.yellow },
          { label: 'BATTERY', u: 5, color: dataBlue },
        ],
      },
      loads: [
        { label: 'HOME', u: 3, color: inkDark },
        { label: 'EVC', u: 3, color: colors.green },
      ],
      checks: ['6u demand', '6u provided'],
    },
    {
      name: 'GRID RELIANT',
      pill: alertRed,
      pillText: '#fff',
      conditions: ['Weak / no solar', 'Battery depleted'],
      supply: { heading: 'GRID CAPPED 5u', color: alertRed, segments: [{ label: 'GRID', u: 5, color: alertRed }] },
      loads: [
        { label: 'HOME', u: 3, color: inkDark },
        { label: 'EVC', u: 2, color: colors.green },
      ],
      checks: ['No overloading', 'Main load priority', 'EVC balanced'],
    },
  ];

  return (
    <Grid container spacing={3}>
      {scenarios.map((sc) => (
        <Grid item xs={12} sm={6} md={3} key={sc.name}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              border: '1px solid #e6e6e6',
              bgcolor: '#fff',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-6px)',
                boxShadow: `0 12px 32px ${sc.pill}30`,
                borderColor: sc.pill,
              },
            }}
          >
            <Box
              sx={{
                alignSelf: 'flex-start',
                bgcolor: sc.pill,
                color: sc.pillText,
                px: 2,
                py: 0.5,
                borderRadius: 5,
                fontWeight: 800,
                fontSize: '0.9rem',
                letterSpacing: 0.5,
              }}
            >
              {sc.name}
            </Box>

            <Box>
              {sc.conditions.map((c) => (
                <Typography
                  key={c}
                  sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#444', lineHeight: 1.7 }}
                >
                  • {c}
                </Typography>
              ))}
            </Box>

            <UnitBar heading={sc.supply.heading} headingColor={sc.supply.color} segments={sc.supply.segments} />
            <UnitBar segments={sc.loads} filled />

            <Box sx={{ mt: 'auto', pt: 1, borderTop: '1px dashed #e0e0e0' }}>
              {sc.checks.map((check) => (
                <Box key={check} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
                  <CheckCircleIcon sx={{ fontSize: 15, color: colors.green }} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#333' }}>
                    {check}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}

/* ------------------------------------------------------------------ */
/* Section — "P&EMS Power & Energy Management System"                  */
/* ------------------------------------------------------------------ */

function PemsSection() {
  const energySteps = [
    'Grid supplies power to the Distribution Board (DB)',
    'Two-way flow — the ESS stores solar and discharges when needed, or tops up from grid',
    'Solar panels generate power, stored in the ESS',
    'DB distributes power to home loads — A/C, lighting, appliances, electronics',
    'DB powers the EV charger — from solar via ESS or grid, balanced in real time to available capacity',
  ];

  const dataSteps = [
    'CT sensor monitors grid import/export and spare capacity, sends data to the EMS',
    'ESS reports battery state of charge, health and solar production to the EMS',
    'EMS sends DLB commands to the EV charger — prevents overload, prioritises home loads, optimizes solar use',
  ];

  return (
    <Box
      id="pems"
      sx={{
        py: { xs: 6, md: 10 },
        bgcolor: colors.white,
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip
            label="P&EMS"
            sx={{
              bgcolor: `${colors.yellow}25`,
              color: '#8a6d00',
              fontWeight: 700,
              letterSpacing: 1,
              fontSize: '0.75rem',
              mb: 2,
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.75rem' },
              fontWeight: 800,
              mb: 2,
              color: colors.black,
            }}
          >
            Power & Energy{' '}
            <Box component="span" sx={{ color: colors.green }}>
              Management System
            </Box>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#666',
              maxWidth: '760px',
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.2rem' },
            }}
          >
            With Dynamic Load Balancing (DLB) control — how energy and data move
            through your system
          </Typography>
        </Box>

        {/* System schematic */}
        <Paper
          elevation={0}
          sx={{
            display: { xs: 'none', sm: 'block' },
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 4,
            border: '1px solid #e6e6e6',
            bgcolor: '#fff',
            mb: 6,
          }}
        >
          <PemsDiagram />
        </Paper>

        {/* Energy & data flow */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <StepList
              title="ENERGY FLOW"
              titleColor={colors.green}
              chipColor={inkDark}
              steps={energySteps}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <StepList
              title="DATA FLOW"
              titleColor={dataBlue}
              chipColor={dataBlue}
              steps={dataSteps}
            />
          </Grid>
        </Grid>

        {/* DLB scenarios */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: '1.6rem', md: '2.1rem' },
              fontWeight: 800,
              mb: 1.5,
              color: colors.black,
            }}
          >
            Real-Time Balancing in{' '}
            <Box component="span" sx={{ color: colors.yellow }}>
              Action
            </Box>
          </Typography>
          <Typography sx={{ color: '#666', maxWidth: '650px', mx: 'auto', fontSize: { xs: '0.95rem', md: '1.05rem' } }}>
            One system, every condition — how DLB allocates power as solar and
            battery levels change through the day
          </Typography>
        </Box>

        <DlbScenarios />

        {/* Benefit strip */}
        <Box
          sx={{
            mt: 6,
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            background: `linear-gradient(135deg, ${colors.black} 0%, ${colors.darkGreen} 100%)`,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 2, md: 5 },
            textAlign: 'center',
          }}
        >
          {[
            { icon: <BoltIcon sx={{ color: colors.yellow }} />, text: 'Prevents overload' },
            { icon: <HomeIcon sx={{ color: colors.yellow }} />, text: 'Prioritises home loads' },
            { icon: <WbSunnyIcon sx={{ color: colors.yellow }} />, text: 'Optimizes solar use' },
            { icon: <EvStationIcon sx={{ color: colors.yellow }} />, text: 'EV charging, balanced' },
          ].map((item) => (
            <Box key={item.text} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {item.icon}
              <Typography sx={{ color: colors.white, fontWeight: 700, fontSize: '1rem' }}>
                {item.text}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

export default PemsSection;
